import api from './api';

export interface BandwidthSummary {
  currentMonth: {
    month: string;
    byService: ServiceBandwidth[];
    totals: BandwidthTotals;
  };
  previousMonth: {
    month: string;
    byService: ServiceBandwidth[];
    totals: BandwidthTotals;
  };
  trends: {
    ingressChange: number;
    egressChange: number;
    costChange: number;
  };
}

export interface ServiceBandwidth {
  serviceName: string;
  totalIngressGB: number;
  totalEgressGB: number;
  estimatedCost: number;
  resourceCount: number;
}

export interface BandwidthTotals {
  totalIngressGB: number;
  totalEgressGB: number;
  estimatedCost: number;
}

export interface BandwidthDetail {
  id?: number;
  userId: number;
  serviceName: string;
  resourceId: string;
  resourceName: string;
  region: string;
  month: string;
  ingressBytes: number;
  egressBytes: number;
  estimatedCost: number;
  lastUpdated: string;
}

export interface BandwidthTrend {
  month: string;
  services: Record<string, {
    ingressGB: number;
    egressGB: number;
    cost: number;
  }>;
  totals: {
    ingressGB: number;
    egressGB: number;
    cost: number;
  };
}

export interface RegionCost {
  region: string;
  services: Record<string, {
    ingressGB: number;
    egressGB: number;
    estimatedCost: number;
    resourceCount: number;
  }>;
  totals: {
    ingressGB: number;
    egressGB: number;
    estimatedCost: number;
  };
}

export const bandwidthService = {
  async getBandwidthSummary(): Promise<BandwidthSummary> {
    const response = await api.get<BandwidthSummary>('/bandwidth/summary');
    return response.data;
  },

  async getServiceBandwidthDetails(serviceName: string, month?: string): Promise<BandwidthDetail[]> {
    const params = new URLSearchParams();
    params.append('serviceName', serviceName);
    if (month) params.append('month', month);
    
    const response = await api.get<BandwidthDetail[]>(`/bandwidth/service-details?${params.toString()}`);
    return response.data;
  },

  async getBandwidthTrends(months: number = 6): Promise<BandwidthTrend[]> {
    const response = await api.get<BandwidthTrend[]>(`/bandwidth/trends?months=${months}`);
    return response.data;
  },

  async getBandwidthCostsByRegion(month?: string): Promise<RegionCost[]> {
    const params = month ? `?month=${month}` : '';
    const response = await api.get<RegionCost[]>(`/bandwidth/costs-by-region${params}`);
    return response.data;
  },

  async syncBandwidthData(credentialId: number, regions: string[]): Promise<{
    message: string;
    resourcesProcessed: number;
    regions: string[];
  }> {
    const response = await api.post('/bandwidth/sync', {
      credentialId,
      regions
    }, {
      timeout: 180000 // 3 minutes timeout for bandwidth sync operations
    });
    return response.data;
  }
};